import React from 'react';
import { Box, Typography } from '@mui/material';
import { Dashboard } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, RowSelectEventArgs, SortDirection } from '@syncfusion/ej2-react-grids';
import { TeamListView } from '../../../../services/TeamService';
import { LoadingIndicator } from '../../../common/components/styled-ui-elements';
import './TeamsGrid.css';

/**
 * Reusable grid component for displaying teams data
 * Can be used across different pages that need to display teams in a grid format
 */
interface TeamsGridProps {
  teams: TeamListView[];
  searchText: string;
  onRowSelect?: (team: TeamListView) => void;
  isLoading?: boolean;
}

const TeamsGrid: React.FC<TeamsGridProps> = ({ teams, searchText, onRowSelect, isLoading = false }) => {
  // Filter teams based on search text
  const filteredTeams = React.useMemo(() => {
    if (searchText.trim() === '') {
      return teams;
    }
    return teams.filter(team =>
      team.name.toLowerCase().includes(searchText.toLowerCase()) ||
      team.description.toLowerCase().includes(searchText.toLowerCase()) ||
      team.status.toLowerCase().includes(searchText.toLowerCase()) ||
      team.createdBy.toLowerCase().includes(searchText.toLowerCase()) ||
      team.modifiedBy.toLowerCase().includes(searchText.toLowerCase()) ||
      (team.assignedCategories || []).some(category => 
        category.toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [teams, searchText]);

  // Handle row click
  const handleRowSelect = (args: RowSelectEventArgs) => {
    const selectedTeam = args.data as TeamListView;
    if (onRowSelect) {
      onRowSelect(selectedTeam);
    }
  };

  if (isLoading) {
    return <LoadingIndicator icon={Dashboard} message="Loading teams..." />;
  }

  if (filteredTeams && filteredTeams.length > 0) {
    return (
      <Box>
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1, 
            color: '#64748b',
            fontStyle: 'italic'
          }}
        >
          Click on any row to edit the team
        </Typography>
        <GridComponent
          dataSource={filteredTeams}
          allowPaging={true}
          allowSorting={true}
          pageSettings={{ pageSize: 20 }}
          sortSettings={{
            columns: [{ field: 'createdDate', direction: 'Descending' as SortDirection }]
          }}
          height={600}
          gridLines="Both"
          rowSelected={handleRowSelect}
          cssClass="clickable-grid"
        >
        <ColumnsDirective>
          <ColumnDirective 
            field="name" 
            headerText="Team Name" 
            width="200"
            textAlign="Left"
            allowSorting={true}
          />
          <ColumnDirective 
            field="description" 
            headerText="Description" 
            width="250"
            textAlign="Left"
            allowSorting={true}
          />
          <ColumnDirective 
            field="status" 
            headerText="Status" 
            width="100"
            textAlign="Center"
            allowSorting={true}
          />
          <ColumnDirective 
            field="memberCount" 
            headerText="Total Members" 
            width="120"
            textAlign="Center"
            allowSorting={true}
          />
          <ColumnDirective 
            field="categoryCount" 
            headerText="Categories" 
            width="100"
            textAlign="Center"
            allowSorting={true}
          />
          <ColumnDirective 
            field="assignedCategories" 
            headerText="Assigned Categories" 
            width="200"
            textAlign="Left"
            allowSorting={false}
            valueAccessor={(field: string, data: any) => {
              const categories = data.assignedCategories || [];
              return categories.length > 0 ? categories.join(', ') : 'None';
            }}
          />

          <ColumnDirective 
            field="createdBy" 
            headerText="Created By" 
            width="150"
            textAlign="Left"
            allowSorting={true}
          />
          <ColumnDirective 
            field="createdDate" 
            headerText="Created Date" 
            width="150"
            textAlign="Center"
            type="date"
            format="MM/dd/yyyy"
            allowSorting={true}
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      textAlign: 'center', 
      py: 4,
      color: '#64748b'
    }}>
      <Typography variant="h6" gutterBottom>
        {searchText ? 'No teams match your search' : 'No teams found'}
      </Typography>
      <Typography variant="body2">
        {searchText 
          ? 'Try adjusting your search criteria.' 
          : 'No teams have been created yet. Click "Add Team" to create your first team.'
        }
      </Typography>
    </Box>
  );
};

export default TeamsGrid;